<automatick-modal-shell
  title="Set up Automatick"
  [size]="modalSize"
  (dismissed)="dismiss()"
>
  <div class="onboarding-modal__content">
    <div class="onboarding-modal__intro">
      <span class="onboarding-modal__intro-icon" aria-hidden="true">
        <i sch-icon="bolt"></i>
      </span>
      <p>Confirm the essentials now or finish later from Account.</p>
    </div>

    @if (isLoading) {
      <div class="onboarding-modal__loading" role="status">
        <i
          sch-icon="autorenew-fill"
          class="onboarding-spin"
          aria-hidden="true"
        ></i>
        <span>Loading setup...</span>
      </div>
    } @else {
      @if (errorMessage) {
        <div class="onboarding-modal__error" role="alert">
          <i sch-icon="alert-circle" aria-hidden="true"></i>
          <span>{{ errorMessage }}</span>
        </div>
      }

      @if (setup) {
        <nav class="onboarding-steps" aria-label="Setup progress">
          <div class="onboarding-steps__track">
            <span class="onboarding-steps__line" aria-hidden="true">
              <span
                class="onboarding-steps__line-fill"
                [style.width.%]="progressPercent"
              ></span>
            </span>

            @for (step of steps; track step.key; let index = $index) {
              <button
                class="onboarding-step"
                type="button"
                [class.is-active]="index === activeStepIndex"
                [class.is-complete]="isStepComplete(step.key)"
                [class.is-skipped]="isStepSkipped(step.key)"
                [disabled]="isBusy"
                (click)="selectStep(index)"
                [attr.aria-current]="index === activeStepIndex ? 'step' : null"
                [attr.aria-label]="step.label"
              >
                <span class="onboarding-step__bubble" aria-hidden="true">
                  @if (isStepComplete(step.key)) {
                    <i sch-icon="checkmark"></i>
                  } @else {
                    <i [attr.sch-icon]="step.icon"></i>
                  }
                </span>
                <span class="onboarding-step__label">{{ step.label }}</span>
              </button>
            }
          </div>
        </nav>

        <seech-stepper
          class="onboarding-stepper"
          [selectedIndex]="activeStepIndex"
          [showHeader]="false"
          (stepChanged)="selectStep($event.index)"
        >
          <seech-step [label]="steps[0].label">
            <div class="onboarding-panel">
              <div class="onboarding-panel__hero">
                <span class="onboarding-panel__hero-icon" aria-hidden="true">
                  <i sch-icon="globe"></i>
                </span>
                <div>
                  <h3>Time zone</h3>
                  <p>
                    Keeps schedules, reminders, and deadlines aligned with your
                    local time.
                  </p>
                </div>
              </div>

              <div class="onboarding-field">
                <label class="onboarding-field__label" for="onboarding-timezone"
                  >Time zone</label
                >
                <seech-select
                  id="onboarding-timezone"
                  [options]="timeZoneOptions"
                  placeholder="Search time zones"
                  [disabled]="isBusy"
                  [(selectedValue)]="timeZoneId"
                ></seech-select>
              </div>

              <p class="onboarding-hint">
                <i sch-icon="info-outline" aria-hidden="true"></i>
                <span
                  >Auto-detected from your browser. Edit it if it looks
                  incorrect.</span
                >
              </p>
            </div>
          </seech-step>

          <seech-step [label]="steps[1].label">
            <div class="onboarding-panel">
              <div class="onboarding-panel__hero">
                <span class="onboarding-panel__hero-icon" aria-hidden="true">
                  <i sch-icon="dashboard-circle"></i>
                </span>
                <div>
                  <h3>Proactive assistant</h3>
                  <p>
                    Let your agent reach out when it has something useful to
                    share.
                  </p>
                </div>
              </div>

              <button
                class="onboarding-toggle-card"
                type="button"
                [class.is-on]="proactiveAssistantEnabled"
                [disabled]="isBusy"
                (click)="proactiveAssistantEnabled = !proactiveAssistantEnabled"
                [attr.aria-pressed]="proactiveAssistantEnabled"
              >
                <span class="onboarding-toggle-card__body">
                  <strong>Enable proactive check-ins</strong>
                  <small
                    >Scheduled results, decision requests, and helpful
                    follow-ups will appear automatically.</small
                  >
                </span>
                <span
                  class="onboarding-toggle"
                  [class.is-on]="proactiveAssistantEnabled"
                  aria-hidden="true"
                >
                  <span class="onboarding-toggle__thumb"></span>
                </span>
              </button>
            </div>
          </seech-step>

          <seech-step [label]="steps[2].label">
            <div class="onboarding-panel">
              <div class="onboarding-panel__hero">
                <span class="onboarding-panel__hero-icon" aria-hidden="true">
                  <i sch-icon="messaging"></i>
                </span>
                <div>
                  <h3>Communication channels</h3>
                  <p>
                    Choose how your agent reaches you when something needs your
                    attention.
                  </p>
                </div>
              </div>

              @if (communicationChannels.length > 0) {
                <div class="onboarding-channel-grid">
                  @for (
                    channel of communicationChannels;
                    track channel.channelKind
                  ) {
                    <button
                      class="onboarding-channel"
                      type="button"
                      [class.is-selected]="
                        isChannelSelected(channel.channelKind)
                      "
                      [class.is-locked]="channel.channelKind === 'InApp'"
                      [disabled]="
                        isBusy ||
                        !channel.isAvailable ||
                        channel.channelKind === 'InApp'
                      "
                      (click)="toggleChannel(channel)"
                      [attr.aria-pressed]="
                        isChannelSelected(channel.channelKind)
                      "
                    >
                      <span class="onboarding-channel__icon" aria-hidden="true">
                        <i
                          [attr.sch-icon]="channelIcon(channel.channelKind)"
                        ></i>
                      </span>
                      <span class="onboarding-channel__body">
                        <strong>{{
                          formatChannelKind(channel.channelKind)
                        }}</strong>
                        <small>
                          {{
                            channel.destination ||
                              channel.unavailableReason ||
                              (channel.channelKind === 'InApp'
                                ? 'Always active'
                                : 'Available')
                          }}
                        </small>
                      </span>
                      <span
                        class="onboarding-channel__check"
                        aria-hidden="true"
                      >
                        <i sch-icon="check-circle"></i>
                      </span>
                    </button>
                  }
                </div>
              } @else {
                <div class="onboarding-empty">
                  <span class="onboarding-empty__icon" aria-hidden="true">
                    <i sch-icon="info-outline"></i>
                  </span>
                  <p>
                    No additional communication channels are available right
                    now.
                  </p>
                </div>
              }
            </div>
          </seech-step>

          <seech-step [label]="steps[3].label">
            <div class="onboarding-panel">
              <div class="onboarding-panel__hero">
                <span class="onboarding-panel__hero-icon" aria-hidden="true">
                  <i sch-icon="hub"></i>
                </span>
                <div>
                  <h3>Devices and connectors</h3>
                  <p>
                    Connect your devices and tools so your agent can act on your
                    behalf.
                  </p>
                </div>
              </div>

              @if (deviceRecommendations.length > 0) {
                <ul class="onboarding-connectors">
                  @for (
                    recommendation of deviceRecommendations;
                    track recommendation.key
                  ) {
                    <li class="onboarding-connector">
                      <span
                        class="onboarding-connector__icon"
                        aria-hidden="true"
                      >
                        <i sch-icon="hub"></i>
                      </span>
                      <span class="onboarding-connector__body">
                        <strong>{{ recommendation.displayName }}</strong>
                        @if (recommendation.instructions?.[0]; as instruction) {
                          <small>{{ instruction }}</small>
                        }
                      </span>
                      <button
                        sch-button
                        size="sm"
                        variant="solid"
                        color="primary"
                        type="button"
                        [disabled]="
                          isBusy || !hasRecommendationTarget(recommendation)
                        "
                        (click)="openRecommendation(recommendation)"
                      >
                        <i sch-icon="arrow-diagonal" aria-hidden="true"></i>
                        <span>Open</span>
                      </button>
                    </li>
                  }
                </ul>
              } @else {
                <div class="onboarding-empty">
                  <span class="onboarding-empty__icon" aria-hidden="true">
                    <i sch-icon="check-circle"></i>
                  </span>
                  <p>No connector setup is required right now.</p>
                </div>
              }
            </div>
          </seech-step>

          <seech-step [label]="steps[4].label">
            <div class="onboarding-panel">
              <div class="onboarding-panel__hero">
                <span
                  class="onboarding-panel__hero-icon onboarding-panel__hero-icon--warning"
                  aria-hidden="true"
                >
                  <i sch-icon="warning"></i>
                </span>
                <div>
                  <h3>Emergency contacts</h3>
                  <p>
                    Choose who your agent should notify in urgent situations.
                  </p>
                </div>
              </div>

              <div class="onboarding-stat-grid">
                <div
                  class="onboarding-stat"
                  [class.onboarding-stat--empty]="emergencyContactCount === 0"
                >
                  <span class="onboarding-stat__value">{{
                    emergencyContactCount
                  }}</span>
                  <span class="onboarding-stat__label">Emergency contacts</span>
                </div>
                <div
                  class="onboarding-stat"
                  [class.onboarding-stat--empty]="
                    emergencyContactMethodCount === 0
                  "
                >
                  <span class="onboarding-stat__value">{{
                    emergencyContactMethodCount
                  }}</span>
                  <span class="onboarding-stat__label">Contact methods</span>
                </div>
              </div>

              <button
                sch-button
                variant="outline"
                size="sm"
                type="button"
                [disabled]="isBusy"
                (click)="openEmergencySettings()"
              >
                <i sch-icon="person-add" aria-hidden="true"></i>
                <span>Manage contacts</span>
              </button>
            </div>
          </seech-step>
        </seech-stepper>
      } @else {
        <div class="onboarding-empty onboarding-empty--setup">
          <span class="onboarding-empty__icon" aria-hidden="true">
            <i sch-icon="alert-circle"></i>
          </span>
          <p>
            Setup details are not available right now. You can try again later
            from Account.
          </p>
        </div>
      }
    }
  </div>

  <div modalActions class="onboarding-modal__actions">
    <button
      sch-button
      variant="outline"
      size="sm"
      type="button"
      [disabled]="isBusy"
      (click)="dismiss()"
    >
      Later
    </button>

    @if (!isLoading && setup) {
      <div class="onboarding-modal__action-group">
        @if (!isLastStep) {
          <button
            sch-button
            variant="outline"
            size="sm"
            type="button"
            [disabled]="isBusy"
            (click)="skipStep()"
          >
            Skip for now
          </button>
        }
        <button
          sch-button
          variant="outline"
          size="sm"
          type="button"
          [disabled]="isBusy || isFirstStep"
          (click)="previous()"
        >
          <i sch-icon="prev-arrow" aria-hidden="true"></i>
          <span>Back</span>
        </button>
        <button
          sch-button
          variant="solid"
          color="primary"
          size="sm"
          type="button"
          [disabled]="isBusy"
          (click)="saveAndContinue()"
        >
          @if (isSubmitting) {
            <i
              sch-icon="autorenew-fill"
              class="onboarding-spin"
              aria-hidden="true"
            ></i>
          }
          <span>{{
            isSubmitting ? 'Saving...' : isLastStep ? 'Finish' : 'Continue'
          }}</span>
          @if (!isSubmitting && !isLastStep) {
            <i sch-icon="next-arrow" aria-hidden="true"></i>
          }
          @if (!isSubmitting && isLastStep) {
            <i sch-icon="checkmark" aria-hidden="true"></i>
          }
        </button>
      </div>
    }
  </div>
</automatick-modal-shell>
